import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Clock, XCircle } from "lucide-react";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    const timer = setInterval(async () => {
      tries++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          clearInterval(timer);
        } else if (tries >= 15) {
          setState("pending");
          clearInterval(timer);
        }
      } catch {
        if (tries >= 15) { setState("error"); clearInterval(timer); }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-asphalt flex items-center justify-center px-5" data-testid="payment-success-page">
      <div className="w-full max-w-md bg-panel border border-[#263342] p-10 text-center">
        <div className="flex justify-center mb-6"><Logo size={40} /></div>
        {state === "checking" && (
          <>
            <Clock size={40} className="text-crimson mx-auto mb-4 animate-pulse" />
            <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl text-slate-100">Confirming payment...</h1>
            <p className="mt-3 text-sm text-slate-400">Hold tight, we're checking with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={40} className="text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl text-slate-100" data-testid="payment-confirmed-heading">Payment received</h1>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Your order is in. Werkhaus will contact you to arrange pickup or fitting.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <Clock size={40} className="text-amber-400 mx-auto mb-4" />
            <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl text-slate-100">Payment processing</h1>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Your bank is still confirming. You'll get an email when it clears.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={40} className="text-red-400 mx-auto mb-4" />
            <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl text-slate-100">Something went wrong</h1>
            <p className="mt-3 text-sm text-slate-400">Contact the workshop and we'll sort it out.</p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-success-home-link"
          className="mt-8 inline-block bg-crimson text-white font-display font-semibold uppercase tracking-widest text-sm px-8 py-3.5 hover:-translate-y-0.5 transition-transform"
        >
          Back to Werkhaus
        </Link>
      </div>
    </div>
  );
}
